import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { industryCollab } from "../../data/placementsExtra";
import assetUrl from "../../services/assetUrl";

// Partner rail (top-left) selects an industry; each industry shows a
// 1 big + 4 small gallery. Click any small image to promote it to the big
// slot; framer-motion `layout` animates the swap. Switching industry
// crossfades the whole gallery. `data` comes from the dynamic page content
// (falls back to the bundled placementsExtra default).
const IndustryCollab = ({ data }) => {
  const reduce = useReducedMotion();
  const [industry, setIndustry] = useState(0);
  const [active, setActive] = useState(0);

  // Only industries that actually have photos are renderable (an empty gallery
  // would crash the big/small layout). Fall back to the bundled set if none.
  const withPhotos = (list) => (list || []).filter((ind) => ind.items && ind.items.length);
  const dynamic = withPhotos(data && data.industries);
  const INDUSTRIES = dynamic.length ? dynamic : withPhotos(industryCollab.industries);
  if (!INDUSTRIES.length) return null;
  const items = (INDUSTRIES[industry] || INDUSTRIES[0]).items;
  // active first (big), the rest follow in their original order
  const ordered = [items[active], ...items.filter((_, i) => i !== active)];
  const swap = reduce ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] };

  const selectIndustry = (i) => { setIndustry(i); setActive(0); };

  return (
    <div className="plx-collab">
      {/* Partner rail */}
      <div className="plx-collab__rail" role="tablist" aria-label="Industry partners">
        {INDUSTRIES.map((ind, i) => {
          const isActive = i === industry;
          return (
            <button
              key={ind.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`plx-collab__partner${isActive ? " is-active" : ""}`}
              onClick={() => selectIndustry(i)}
            >
              <span className="plx-collab__partner-thumb">
                <img src={assetUrl(ind.items[0].src)} alt="" loading="lazy" />
              </span>
              <span className="plx-collab__partner-txt">
                <strong>{ind.name}</strong>
                <span>{ind.items.length} photos</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Swap gallery for the selected partner */}
      <AnimatePresence mode="wait">
        <motion.div
          key={industry}
          className="plx-collab__grid"
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, transition: { duration: reduce ? 0 : 0.18 } }}
          transition={{ duration: reduce ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          {ordered.map((it, pos) => {
            const big = pos === 0;
            const idx = items.indexOf(it);
            return (
              <motion.figure
                layout
                key={it.src}
                transition={swap}
                className={`plx-collab__card${big ? " is-big" : ""}`}
                onClick={() => !big && setActive(idx)}
                role={big ? undefined : "button"}
                tabIndex={big ? -1 : 0}
                aria-label={big ? undefined : `Show ${it.tag}`}
                onKeyDown={(e) => { if (!big && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setActive(idx); } }}
              >
                <img className="plx-collab__bg" src={assetUrl(it.src)} alt="" aria-hidden="true" loading="lazy" />
                <img className="plx-collab__img" src={assetUrl(it.src)} alt={it.caption} loading="lazy" />
                <figcaption className="plx-collab__cap">
                  <span className="plx-collab__tag">{it.tag}</span>
                  <span className="plx-collab__text">{it.caption}</span>
                </figcaption>
                {!big && <span className="plx-collab__hint" aria-hidden="true">＋</span>}
              </motion.figure>
            );
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default IndustryCollab;
